"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import Image from "next/image";
import Link from "next/link";
import { useAxiosPrivate } from "@/api/axios";
import Loader from "@/components/loader";
import Card from "@/components/HotelCard";

const MapWithMarkers = ({
  availableProperties,
  width = "400px",
  height = "100%",
  fullPage = false,
}) => {
  const [locations, setLocations] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 25.2048,
    lng: 55.2708,
  });
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const previousBounds = useRef(null);
  const cardRef = useRef(null);
  const axios = useAxiosPrivate();

  useEffect(() => {
    setLocations(
      availableProperties.map((property) => ({
        id: property?.property?._id,
        latitude: +property?.property?.location[1] || 0,
        longitude: +property?.property?.location[0] || 0,
        label: property?.property?.propertyName || "",
        data: property || {},
      }))
    );
    if (availableProperties.length > 0 && !fullPage) {
      setMapCenter({
        lat: availableProperties[0]?.property?.location[1],
        lng: availableProperties[0]?.property?.location[0],
      });
    }
  }, [availableProperties]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  });

  const handleMapClick = () => {
    // Close the active card when clicking outside
    setActiveMarker(null);
  };

  const handleMarkerClick = (location) => {
    setActiveMarker(location.id);
    setMapCenter({
      lat: +location.latitude + 0.05, // Adjust latitude slightly to move the pin towards the bottom
      lng: location.longitude,
    });
  };

  const fetchPropertiesByBounds = useCallback(async () => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const newBounds = {
        northEast: { lat: ne.lat(), lng: ne.lng() },
        southWest: { lat: sw.lat(), lng: sw.lng() },
      };

      // Prevent unnecessary fetch if bounds haven't changed
      if (
        previousBounds.current &&
        JSON.stringify(previousBounds.current) === JSON.stringify(newBounds)
      ) {
        return;
      }

      // Update previous bounds
      previousBounds.current = newBounds;

      try {
        setLoading(true);
        const start_date = JSON.parse(localStorage.getItem("mainState"))
          .bookingInfo.start_date;
        const end_date = JSON.parse(localStorage.getItem("mainState"))
          .bookingInfo.end_date;
        const response = await axios.get("/api/guest/property/getAllProperty", {
          params: { ...newBounds, start_date, end_date },
        });
        setLocations(
          response.data.data.map((property) => ({
            id: property?.property?._id,
            latitude: +property?.property?.location[1],
            longitude: +property?.property?.location[0],
            label: property?.property?.propertyName,
            data: property,
          }))
        );
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const onMapIdle = useCallback(() => {
    if (fullPage) fetchPropertiesByBounds();
  }, [fullPage, fetchPropertiesByBounds]);

  if (!isLoaded) {
    return null;
  }

  return (
    <>
      {fullPage ? (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "20px",
            zIndex: 10,
            padding: "5px",
            borderRadius: "50%",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading ? <Loader /> : null}
        </div>
      ) : (
        <Link
          href="/mapSearch"
          style={{
            position: "absolute",
            top: "10px",
            left: "20px",
            zIndex: 10,
            background: "#fff",
            padding: "5px",
            borderRadius: "50%",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
            cursor: "pointer",
            width: "40px",
            height: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Image
            src="/assets/icons/svgs/open-map.svg"
            width={24}
            height={24}
            alt="open map"
          />
        </Link>
      )}
      <GoogleMap
        mapContainerStyle={{ width, height }}
        center={mapCenter}
        zoom={12}
        onIdle={onMapIdle}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
        }}
        onLoad={(map) => (mapRef.current = map)}
        onClick={handleMapClick} // Close the card when clicking outside
      >
        {locations.map((location) => (
          <React.Fragment key={location.id}>
            <Marker
              position={{ lat: location.latitude, lng: location.longitude }}
              onClick={() => handleMarkerClick(location)} // Open card on click
              icon={{
                url: "/assets/icons/svgs/custom-marker.svg",
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
            {activeMarker === location.id && (
              <OverlayView
                position={{ lat: location.latitude, lng: location.longitude }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  ref={cardRef}
                  className="map-card"
                  onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the card
                >
                  <Card
                    previewImages={location.data.images}
                    property={location.data.property}
                    availability={location.data.availability}
                  />
                </div>
              </OverlayView>
            )}
          </React.Fragment>
        ))}
      </GoogleMap>
    </>
  );
};

export default MapWithMarkers;
